import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core'

const useStyles = makeStyles(() => ({
  tableContainer: {
    maxHeight: '400px',
    overflow: 'auto',
    margin: '16px 0',
    border: '1px solid var(--color-neutral-400)',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--dai-color-surface4)'
  },
  table: {
    minWidth: 500,
    borderCollapse: 'separate',
    borderSpacing: 0,
    backgroundColor: 'transparent'
  },
  tableRow: {
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'var(--dai-color-surface3)'
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'var(--dai-color-surface4)'
    },
    '&:nth-of-type(even):hover': {
      backgroundColor: 'var(--dai-color-surface3)'
    }
  },
  tableCell: {
    fontSize: 'var(--font-size-sm)',
    padding: '10px 16px',
    color: 'var(--dai-color-text)',
    borderBottom: '1px solid var(--color-neutral-200)',
    '&:first-child': {
      paddingLeft: '20px'
    },
    '&:last-child': {
      paddingRight: '20px'
    }
  },
  headerRow: {
    backgroundColor: 'var(--dai-color-input)',
    '&:hover': {
      backgroundColor: 'var(--dai-color-input)'
    }
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: 'var(--dai-color-input)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    color: 'var(--color-primary-700)',
    borderBottom: '2px solid var(--color-neutral-300)',
    fontSize: 'var(--font-size-sm)',
    padding: '12px 16px',
    '&:first-child': {
      paddingLeft: '20px'
    },
    '&:last-child': {
      paddingRight: '20px'
    }
  }
}))

/**
 * InlineTable - A table component for embedding in markdown/messages
 *
 * Unlike other table components which require complex configurations or data providers,
 * InlineTable accepts a simple array of objects and column definitions. This is ideal
 * for AI-generated visualizations or when you need to display tabular data inline.
 *
 * @example
 * <InlineTable
 *   data={[
 *     { name: 'John', age: 30, city: 'New York' },
 *     { name: 'Jane', age: 25, city: 'Boston' }
 *   ]}
 *   columns={[
 *     { key: 'name', label: 'Name' },
 *     { key: 'age', label: 'Age' },
 *     { key: 'city', label: 'City' }
 *   ]}
 *   maxHeight="300px"
 * />
 */
function InlineTable ({ data, maxHeight, columns }) {
  const classes = useStyles()

  if (!Array.isArray(data) || data.length === 0) {
    return null
  }

  if (!Array.isArray(columns) || columns.length === 0) {
    return null
  }

  return (
    <TableContainer className={classes.tableContainer} style={{ maxHeight }}>
      <Table className={classes.table} size='small' stickyHeader>
        <TableHead>
          <TableRow className={classes.headerRow}>
            {columns.map((col) => (
              <TableCell key={col.key} className={classes.headerCell}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={index} className={classes.tableRow}>
              {columns.map((col) => (
                <TableCell key={col.key} className={classes.tableCell}>
                  {typeof row[col.key] === 'object' && row[col.key] !== null
                    ? JSON.stringify(row[col.key])
                    : String(row[col.key] ?? '')}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

InlineTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  maxHeight: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })).isRequired
}

export default InlineTable
